<script setup>
import {
  ref, computed, watch, inject,
} from 'vue';
import { usuarios } from 'cifra-api-js';
import { marcarTodos } from '@/libs/checkboxes';
import { porcentaje } from '@/libs/cifra/importes';

const $I18n = inject('I18n');

const props = defineProps({
  tiposPermitidos: { type: Array, default: () => ([]) },
  fullNameFormat: { type: Boolean, default: false },
  icon: { type: Boolean, default: false },
  titulo: { type: Boolean, default: true },
  formActions: { type: Boolean, default: true },
  // Si es true, el botón aceptar es de tipo submit
  submitAlAceptar: { type: Boolean, default: false },
  // Si es true, el botón de cancelar oculta el modal automáticamente
  ocultarModal: { type: Boolean, default: false },
  seleccionMultiple: { type: Boolean, default: true },
  // Nombre del imput para usuario de la búsqueda
  busquedaInputName: { type: String, default: null },
  // Nombre del input para los usuarios seleccionados
  seleccionInputName: { type: String, default: null },
});

const emits = defineEmits(['cancelar', 'aceptar']);

// Refs
const selectorUsuarios = ref(null);
const tablaResultados = ref(null);

// Data reactiva
const usuarioActualId = ref(null);
const resultados = ref([]);
const searching = ref(false);

// Helpers
const { fullname } = usuarios;

// Methods
const filtrarUsuarios = (usuariosParentesco) => {
  let usuariosFiltrados = usuariosParentesco;
  if (props.tiposPermitidos.length) {
    usuariosFiltrados = usuariosParentesco.filter(
      (usuarioParentesco) => (
        (props.tiposPermitidos.includes('tutor') && usuarioParentesco.tutor)
        || (props.tiposPermitidos.includes('pagador') && usuarioParentesco.pagador)
        || (props.tiposPermitidos.includes('pagado') && usuarioParentesco.pagado)
        || (props.tiposPermitidos.includes('alumno') && usuarioParentesco.usuario.caracteristicas.alumno)
        || (props.tiposPermitidos.includes('padre') && usuarioParentesco.usuario.padre)
        || (props.tiposPermitidos.includes('profesor') && usuarioParentesco.usuario.caracteristicas.profesor)
        // Tipo especial para indicar que hay que mostrar el usuario buscado
        || (props.tiposPermitidos.includes('buscado') && usuarioParentesco.buscado)
        // hijo, padre, madre, hermano, abuelo, otro
        || props.tiposPermitidos.includes(usuarioParentesco.parentesco)
      ),
    );
  }
  return usuariosFiltrados;
};

const usuarioActual = () => selectorUsuarios.value.usuarioActual;

const sinResultados = () => (
  (usuarioActualId.value && resultados.value.length === 0) && !searching.value
);

const tipos = () => {
  if (props.tiposPermitidos.length) {
    const traducidos = props.tiposPermitidos.map((tipo) => {
      let traduccion = $I18n.t('Usuarios');
      switch (tipo) {
        case 'tutor':
          traduccion = $I18n.t('Tutores');
          break;
        case 'pagador':
          traduccion = $I18n.t('Pagadores');
          break;
        case 'pagado':
          traduccion = $I18n.t('pagados');
          break;
        case 'alumno':
          traduccion = $I18n.t('Alumnos');
          break;
        case 'profesor':
          traduccion = $I18n.t('Profesores');
          break;
        case 'hijo':
        case 'padre':
        case 'madre':
        case 'hermano':
        case 'abuelo':
        case 'otro':
          // pluralizamos a mano
          traduccion = `${$I18n.t(tipo, { scope: 'parentescos' })}s`;
          break;
        default:
          return $I18n.t('Usuarios');
      }
      return traduccion.toLowerCase();
    });
    return traducidos.join(' / ');
  }
  return $I18n.t('Usuarios').toLowerCase();
};

const tituloColumnaUsuario = computed(() => {
  // Quitamos el tipo especial "buscado" suelen poner ['pagador', 'buscado'] o similar
  const tiposFiltrados = props.tiposPermitidos.filter((tipo) => tipo !== 'buscado');
  if (tiposFiltrados.length === 1) {
    const tipo = tiposFiltrados[0];
    switch (tipo) {
      case 'tutor':
        return $I18n.t('Tutor');
      case 'pagador':
        return $I18n.t('Pagador');
      case 'pagado':
        return $I18n.t('pagado');
      case 'alumno':
        return $I18n.t('Alumno');
      case 'profesor':
        return $I18n.t('Profesor');
      default:
        return $I18n.t('Usuario');
    }
  }
  return $I18n.t('Usuario');
});

const labelPagadorPagado = (usuarioRelacion) => {
  let label = '';
  if (usuarioRelacion.pagador) {
    label = $I18n.t('Pagador');
  } else if (usuarioRelacion.pagado) {
    label = $I18n.t('pagador_le_paga');
  } else {
    label = '';
  }
  if (usuarioRelacion.porcentaje_pago) {
    label = `${label} ${porcentaje(usuarioRelacion.porcentaje_pago)}`;
  }
  return label;
};

const classPagadorPagado = (usuarioRelacion) => {
  if (usuarioRelacion.porcentaje_pago === 100) {
    return 'label label-success';
  }
  return 'label label-default';
};

// Computed
const seleccionados = computed(() => {
  const marcados = resultados.value.filter((resultado) => resultado.seleccionado);
  marcados.forEach((marcado) => {
    marcado.usuario.fullname = usuarios.fullname(marcado.usuario, props.fullNameFormat);
  });
  return marcados;
});

const isButtonAcceptDisabled = computed(() => seleccionados.value.length === 0);

const aceptar = (e) => {
  if (props.submitAlAceptar) {
    return;
  }
  e.preventDefault();
  emits('aceptar', seleccionados.value);
};

// Watchers
watch(usuarioActualId, (nuevoUsuarioId) => {
  resultados.value = [];
  if (nuevoUsuarioId) {
    searching.value = true;
    usuarios.parentescos(nuevoUsuarioId).then((result) => {
      searching.value = false;
      // Siempre añadimos el usuario de la búsqueda
      resultados.value.push({
        pagado: null,
        pagador: null,
        parentesco: null,
        tutor: false,
        buscado: true,
        usuario: usuarioActual(),
      });

      resultados.value = filtrarUsuarios(resultados.value.concat(result.data));
    });
  }
});

function actualizarSeleccion(usuarioParentesco, seleccionado) {
  if (seleccionado && !props.seleccionMultiple) {
    // Si no es selección múltiple, desmarcamos el resto
    resultados.value.forEach((up) => {
      up.seleccionado = false;
    });
  }
  usuarioParentesco.seleccionado = seleccionado;
}
</script>

<template>
  <div>
    <div v-if="titulo" class="titulo-seccion m-t-none">
      {{ $I18n.t('buscador_usuarios_relacionados', { tipo: tipos() }) }}
    </div>
    <div>
      <selector-usuario
        ref="selectorUsuarios"
        v-model="usuarioActualId"
        :full-name-format="fullNameFormat"
        :icon="icon"
        :input-name="busquedaInputName"
      />
    </div>
    <div class="m-t resultados">
      <div
        v-if="sinResultados()"
        class="alert alert-info"
        role="alert"
      >
        {{ $I18n.t(
          'buscador_usuarios_sin_resultados',
          { tipo: tipos(), usuario: usuarioActual().fullname }
        )
        }}
      </div>
      <table v-if="resultados.length > 0" ref="tablaResultados" class="table">
        <thead>
          <tr>
            <th class="col-min text-center">
              <button
                v-if="seleccionMultiple"
                type="button"
                class="btn btn-xs btn-default as-check"
                @click="marcarTodos(tablaResultados)"
              >
                <i class="far fa-check-square"></i>
              </button>
            </th>
            <th class="usuario">
              {{ tituloColumnaUsuario }}
            </th>
            <th class="col-min text-center">
              {{ $I18n.t('parentesco') }}
            </th>
            <th class="col-min text-center">
              {{ $I18n.t('caracteristicas') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="usuarioParentesco in resultados" :key="usuarioParentesco.usuario.id">
            <td class="col-min text-center">
              <input-checkbox-boolean
                :model-value="usuarioParentesco.seleccionado"
                :input-id="`check-${usuarioParentesco.usuario.id}`"
                :name="seleccionInputName"
                :value="usuarioParentesco.usuario.id"
                class="check-usuario"
                @update:model-value="actualizarSeleccion(usuarioParentesco, $event)"
              />
            </td>
            <td>
              <label :for="`check-${usuarioParentesco.usuario.id}`" class="clickable">
                {{ fullname(usuarioParentesco.usuario, fullNameFormat) }}
              </label>
            </td>
            <td class="col-min text-center">
              <span v-if="usuarioParentesco.parentesco" class="label label-default">
                {{ $I18n.t(usuarioParentesco.parentesco, { scope: 'parentescos' }) }}
              </span>
            </td>
            <td class="col-min text-center">
              <span v-if="usuarioParentesco.pagador" :class="classPagadorPagado(usuarioParentesco)">
                {{ labelPagadorPagado(usuarioParentesco) }}
              </span>
              <span v-if="usuarioParentesco.pagado" :class="classPagadorPagado(usuarioParentesco)">
                {{ labelPagadorPagado(usuarioParentesco) }}
              </span>
              <span v-if="usuarioParentesco.tutor" class="label label-default">
                {{ $I18n.t('Tutor') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="formActions" class="form-actions m-t-md">
      <button
        class="btn btn-default"
        :data-dismiss="ocultarModal ? 'modal' : null"
        @click.prevent="$emit('cancelar')"
      >
        {{ $I18n.t('Cancelar') }}
      </button>
      <button
        class="btn btn-cifra"
        :type="submitAlAceptar ? 'submit' : 'button'"
        :disabled="isButtonAcceptDisabled"
        @click="aceptar"
      >
        {{ $I18n.t('Aceptar') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "../../../assets/stylesheets/cifra/vars/main.scss";

  .selector-parentesco-wrapper{
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .resultados {
    table {
      td span {
        margin-left: $margin-xs;
      }
      td .check-usuario {
        margin: 0;
      }
    }
  }
</style>
